import {
  doc,
  getDoc,
  getDocs,
  collection,
  writeBatch,
  increment,
  deleteField,
  serverTimestamp,
  Timestamp,
  query,
  orderBy,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  buildTournamentEventData,
  createTournamentEventRef,
} from "../tournamentEventLog";

function getNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function getPositiveInt(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.floor(n);
}

function addEventToBatch(batch, { db, tournamentId, ...event }) {
  const eventRef = createTournamentEventRef(db, tournamentId);
  batch.set(eventRef, buildTournamentEventData(event));
}

function getPaidEntryCount({ tournament, entrants }) {
  const explicit = getPositiveInt(
    tournament?.paidEntryCount ||
      tournament?.entriesPaidCount ||
      tournament?.accounting?.paidEntryCount ||
      0,
    0
  );

  if (explicit > 0) return explicit;

  const entrantsCount = getPositiveInt(tournament?.entrantsCount || 0, 0);
  if (entrantsCount > 0) return entrantsCount;

  return Array.isArray(entrants) ? entrants.length : 0;
}

function getTotalRebuys({ tournament, entrants }) {
  const explicit = getNumber(
    tournament?.totalRebuys || tournament?.accounting?.totalRebuys || 0,
    0
  );
  if (explicit > 0) return explicit;

  return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
    return sum + Math.max(0, getNumber(e?.rebuys || 0, 0));
  }, 0);
}

function getTotalAddOns({ tournament, entrants }) {
  const explicit = getNumber(
    tournament?.totalAddOns || tournament?.accounting?.totalAddOns || 0,
    0
  );
  if (explicit > 0) return explicit;

  return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
    return sum + (e?.addOnTaken ? 1 : 0);
  }, 0);
}

function getTotalDealerAppreciationPrizePool({ tournament, entrants }) {
  const explicit = getNumber(
    tournament?.totalDealerAppreciationPrizePool ||
      tournament?.accounting?.totalDealerAppreciationPrizePool ||
      0,
    0
  );
  if (explicit > 0) return explicit;

  return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
    return sum + Math.max(0, getNumber(e?.dealerAppreciationPrizePoolAmount || 0, 0));
  }, 0);
}

function computePrizePoolFromEntrants({ tournament, entrants }) {
  const paidEntryCount = getPaidEntryCount({ tournament, entrants });
  const buyIn = getNumber(tournament?.buyIn || 0, 0);
  const addOnCost = getNumber(tournament?.addOnCost || 0, 0);

  const totalRebuys = getTotalRebuys({ tournament, entrants });
  const totalAddOns = getTotalAddOns({ tournament, entrants });
  const totalDealerAppreciationPrizePool = getTotalDealerAppreciationPrizePool({
    tournament,
    entrants,
  });

  const explicitPrizePool = getNumber(
    tournament?.finalPrizePool || tournament?.prizePool || 0,
    0
  );

  const finalPrizePool =
    explicitPrizePool > 0
      ? explicitPrizePool
      : Math.max(paidEntryCount * buyIn, 0) +
        Math.max(totalRebuys * buyIn, 0) +
        Math.max(totalAddOns * addOnCost, 0) +
        Math.max(totalDealerAppreciationPrizePool, 0);

  return {
    paidEntryCount,
    totalRebuys,
    totalAddOns,
    totalDealerAppreciationPrizePool,
    finalPrizePool,
  };
}

function computeFirstPlacePrizeFromTournament({ tournament, finalPrizePool }) {
  const payouts = Array.isArray(tournament?.payouts) ? tournament.payouts : [];
  const firstPlace = payouts.find((p) => Number(p?.place) === 1);
  if (!firstPlace) return 0;

  const percent = Number(firstPlace?.percent || 0);
  return Math.round(Number(finalPrizePool || 0) * (percent / 100));
}

export async function removeEntrantWhileRegistering({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  isRegistering,
  targetEntrant,
  entrantLabel,
  setEntrants,
  setTournament,
}) {
  if (!isAdmin || isCompleted) return;
  if (!isRegistering) return alert("You can only remove entrants while registering.");

  const label = entrantLabel(targetEntrant);
  const ok = window.confirm(`Remove ${label} from this tournament signup list?`);
  if (!ok) return;

  setEntrants((prev) => prev.filter((p) => p.id !== targetEntrant.id));
  setTournament((prev) => ({
    ...prev,
    paidEntryCount: Math.max(
      Number(prev?.paidEntryCount || prev?.entrantsCount || 0) - 1,
      0
    ),
  }));

  try {
    const batch = writeBatch(db);
    const entrantRef = doc(db, "tournaments", tournamentId, "entrants", targetEntrant.id);
    const tournamentRef = doc(db, "tournaments", tournamentId);

    batch.delete(entrantRef);

    const tournamentUpdates = {
      // entrantsCount is maintained from the actual entrant documents by Cloud Functions.
      paidEntryCount: increment(-1),
      updatedAt: serverTimestamp(),
    };

    if (targetEntrant?.dealerAppreciationOptIn) {
      tournamentUpdates.totalDealerAppreciationOptIns = increment(-1);
      tournamentUpdates.totalDealerAppreciationPrizePool = increment(
        -Math.max(0, getNumber(targetEntrant?.dealerAppreciationPrizePoolAmount || 0, 0))
      );
      tournamentUpdates.totalDealerAppreciationDealerPayout = increment(
        -Math.max(0, getNumber(targetEntrant?.dealerAppreciationDealerAmount || 0, 0))
      );
    }

    batch.update(tournamentRef, tournamentUpdates);
    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "entrant_removed",
      title: `${label} removed from the tournament`,
      playerUid: targetEntrant?.uid || targetEntrant?.id || null,
      playerName: label,
      message: "The entrant was removed while registration was open.",
    });
    await batch.commit();
  } catch (err) {
    console.error("Remove entrant failed:", err);
    alert("Could not remove entrant. Check permissions.");

    try {
      const entrantsRef = collection(db, "tournaments", tournamentId, "entrants");
      const entrantsQ = query(entrantsRef, orderBy("createdAt", "asc"));
      const snap = await getDocs(entrantsQ);
      const eData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setEntrants(eData);
    } catch {
      // ignore
    }
  }
}

export async function addEntrantByAdmin({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  entrantUidSet,
  member,
  options = {},
  setTournament,
}) {
  if (!isAdmin || isCompleted) return;
  if (!member?.uid) return;

  const uid = String(member.uid);
  if (entrantUidSet.has(uid)) return;

  const displayName = member.name || member.email || uid;

  try {
    const tournamentRef = doc(db, "tournaments", tournamentId);
    const tournamentSnap = await getDoc(tournamentRef);

    if (!tournamentSnap.exists()) {
      return alert("Tournament not found.");
    }

    const tournamentData = tournamentSnap.data() || {};

    const earlyBirdTs =
      tournamentData?.earlyBirdDeadline || tournamentData?.earlyBirdCutoffAt || null;

    const cutoffMillis =
      typeof earlyBirdTs?.toMillis === "function" ? earlyBirdTs.toMillis() : null;

    const qualifiesForEarlyBird =
      !!tournamentData?.earlyBirdEnabled &&
      Number(tournamentData?.earlyBirdBonusChips || 0) > 0 &&
      cutoffMillis != null &&
      Date.now() <= cutoffMillis;

    const earlyBirdBonusChips = qualifiesForEarlyBird
      ? Math.max(0, Number(tournamentData?.earlyBirdBonusChips || 0))
      : 0;

    const dealerAppreciationEnabled =
      !!tournamentData?.dealerAppreciationEnabled &&
      Number(tournamentData?.dealerAppreciationCost || 0) > 0;

    const dealerAppreciationOptIn =
      dealerAppreciationEnabled && !!options?.dealerAppreciationOptIn;

    const dealerAppreciationCost = dealerAppreciationOptIn
      ? Math.max(0, Number(tournamentData?.dealerAppreciationCost || 0))
      : 0;

    const dealerAppreciationBonusChips = dealerAppreciationOptIn
      ? Math.max(0, Number(tournamentData?.dealerAppreciationBonusChips || 0))
      : 0;

    const dealerAppreciationPrizePoolAmount = dealerAppreciationOptIn
      ? Math.max(0, Number(tournamentData?.dealerAppreciationPrizePoolAmount || 0))
      : 0;

    const dealerAppreciationDealerAmount = dealerAppreciationOptIn
      ? Math.max(0, Number(tournamentData?.dealerAppreciationDealerAmount || 0))
      : 0;

    const entrantRef = doc(db, "tournaments", tournamentId, "entrants", uid);

    const batch = writeBatch(db);

    batch.set(
      entrantRef,
      {
        uid,
        name: member.name || displayName,
        email: member.email || "",
        displayName,
        profilePhotoUrl: member.profilePhotoUrl || "",

        rebuys: 0,
        eliminated: false,
        finishPlace: 0,
        addOnTaken: false,

        earlyBirdQualified: qualifiesForEarlyBird,
        earlyBirdBonusChips,

        dealerAppreciationOptIn,
        dealerAppreciationCost,
        dealerAppreciationBonusChips,
        dealerAppreciationPrizePoolAmount,
        dealerAppreciationDealerAmount,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        addedByAdmin: true,
      },
      { merge: true }
    );

    const tournamentUpdates = {
      // entrantsCount is maintained from the actual entrant documents by Cloud Functions.
      paidEntryCount: increment(1),
      updatedAt: serverTimestamp(),
    };

    if (String(tournamentData?.status || "") === "running") {
      tournamentUpdates.playersRemaining = increment(1);
    }

    if (dealerAppreciationOptIn) {
      tournamentUpdates.totalDealerAppreciationOptIns = increment(1);
      tournamentUpdates.totalDealerAppreciationPrizePool = increment(
        dealerAppreciationPrizePoolAmount
      );
      tournamentUpdates.totalDealerAppreciationDealerPayout = increment(
        dealerAppreciationDealerAmount
      );
    }

    batch.set(tournamentRef, tournamentUpdates, { merge: true });
    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "entrant_added",
      title: `${displayName} added to the tournament`,
      playerUid: uid,
      playerName: displayName,
      message: dealerAppreciationOptIn
        ? "Added by an administrator with dealer appreciation."
        : "Added by an administrator.",
      metadata: { dealerAppreciationOptIn },
    });
    await batch.commit();

    setTournament((prev) => ({
      ...prev,
      paidEntryCount:
        Number(prev?.paidEntryCount || prev?.entrantsCount || 0) + 1,
    }));
  } catch (e) {
    console.error("Admin add entrant failed:", e);
    alert("Could not add entrant. Check permissions / rules.");
  }
}

export async function setDealerAppreciationForEntrant({
  functions,
  tournamentId,
  isAdmin,
  isCompleted,
  isRegistering,
  targetEntrant,
  tournament,
  entrantLabel,
  formatMoney,
  enabled,
}) {
  if (!isAdmin || isCompleted) return;
  if (!isRegistering) {
    return alert("Dealer appreciation can only be changed while registering.");
  }
  if (!targetEntrant?.id) return;

  const label = entrantLabel(targetEntrant);
  const currentlyEnabled = targetEntrant.dealerAppreciationOptIn === true;
  if (currentlyEnabled === enabled) return;

  const source = enabled ? tournament : targetEntrant;
  const cost = Math.max(0, Number(source?.dealerAppreciationCost || 0));
  const bonus = Math.max(0, Number(source?.dealerAppreciationBonusChips || 0));
  const prizePoolAmount = Math.max(
    0,
    Number(source?.dealerAppreciationPrizePoolAmount || 0)
  );
  const dealerAmount = Math.max(
    0,
    Number(source?.dealerAppreciationDealerAmount || 0)
  );
  const prompt = enabled
    ? `Add Dealer Appreciation for ${label}?\n\nCost: ${formatMoney(cost)}\nBonus Chips: +${bonus.toLocaleString()}\nPrize Pool: ${formatMoney(prizePoolAmount)}\nDealers: ${formatMoney(dealerAmount)}`
    : `Remove Dealer Appreciation from ${label}?\n\nThis reverses the bonus and both accounting contributions${cost > 0 ? ` (${formatMoney(cost)} total)` : ""}.`;
  const ok = window.confirm(prompt);
  if (!ok) return;

  try {
    const control = httpsCallable(functions, "adminTournamentControl");
    const result = await control({
      tournamentId,
      action: "set_dealer_appreciation",
      entrantId: targetEntrant.id,
      enabled,
    });
    return result?.data || { ok: true };
  } catch (e) {
    console.error("Update dealer appreciation failed:", e);
    alert(e?.message || "Could not update dealer appreciation.");
    return null;
  }
}

export async function rebuyEntrant({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  isRunning,
  rebuysEnabled,
  rebuysWindowOpen,
  targetEntrant,
  entrantLabel,
}) {
  if (!isAdmin || isCompleted) return;
  if (!isRunning) return alert("Rebuys are only allowed while running.");
  if (!rebuysEnabled) return alert("This tournament is a freezeout.");
  if (!rebuysWindowOpen) return alert("Rebuy period has ended.");

  if (!targetEntrant?.id) return;
  if (!targetEntrant.eliminated) return alert("That player is already active.");

  const label = entrantLabel(targetEntrant);
  const ok = window.confirm(`Rebuy ${label}? This adds one buy-in to the prize pool.`);
  if (!ok) return;

  try {
    const batch = writeBatch(db);

    const tRef = doc(db, "tournaments", tournamentId);
    const entrantRef = doc(db, "tournaments", tournamentId, "entrants", targetEntrant.id);

    batch.update(entrantRef, {
      eliminated: false,
      finishPlace: 0,
      winner: false,

      eliminatedAt: deleteField(),
      eliminatedByUid: deleteField(),
      eliminatedByName: deleteField(),

      rebuys: increment(1),
      updatedAt: serverTimestamp(),
    });

    batch.update(tRef, {
      playersRemaining: increment(1),
      totalRebuys: increment(1),
      updatedAt: serverTimestamp(),
    });

    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "rebuy",
      title: `${label} rebought`,
      playerUid: targetEntrant?.uid || targetEntrant?.id || null,
      playerName: label,
      message: "One rebuy was added and the player returned to active play.",
    });

    await batch.commit();
  } catch (e) {
    console.error("Rebuy failed:", e);
    alert("Could not rebuy player.");
  }
}

export async function addOnEntrant({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  isRunning,
  addOnsEnabled,
  addOnLevelIndex,
  addOnsWindowOpen,
  addOnChips,
  addOnCost,
  targetEntrant,
  entrantLabel,
  formatMoney,
}) {
  if (!isAdmin || isCompleted) return;
  if (!isRunning) return alert("Add-ons are only allowed while running.");
  if (!addOnsEnabled) return alert("Add-ons are not enabled for this tournament.");
  if (addOnLevelIndex == null) return alert("Add-on level is not configured.");
  if (!addOnsWindowOpen) {
    return alert("Add-ons are not available yet (wait until the selected level ends).");
  }

  if (!targetEntrant?.id) return;
  if (targetEntrant.eliminated) return alert("Only active players can take an add-on.");
  if (targetEntrant.addOnTaken) return alert("This player already took their add-on.");

  const label = entrantLabel(targetEntrant);
  const ok = window.confirm(
    `Add-on for ${label}? (+${Number(addOnChips).toLocaleString()} chips, ${formatMoney(
      addOnCost
    )})`
  );
  if (!ok) return;

  try {
    const batch = writeBatch(db);
    const entrantRef = doc(db, "tournaments", tournamentId, "entrants", targetEntrant.id);
    const tournamentRef = doc(db, "tournaments", tournamentId);

    batch.update(entrantRef, {
      addOnTaken: true,
      addOnTakenAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    batch.set(
      tournamentRef,
      {
        totalAddOns: increment(1),
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "add_on",
      title: `${label} took an add-on`,
      playerUid: targetEntrant?.uid || targetEntrant?.id || null,
      playerName: label,
      message: `+${Number(addOnChips || 0).toLocaleString()} chips for ${formatMoney(addOnCost)}.`,
      metadata: { addOnChips: Number(addOnChips || 0), addOnCost: Number(addOnCost || 0) },
    });

    await batch.commit();
  } catch (e) {
    console.error("Add-on failed:", e);
    alert("Could not apply add-on.");
  }
}

export async function eliminateEntrant({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  isRunning,
  entrants,
  activeEntrants,
  eliminatedEntrant,
  eliminatorId,
  setTournament,
  entrantLabel,
}) {
  if (!isAdmin || isCompleted) return;
  if (!isRunning) {
    return alert("Eliminations are only allowed once the tournament is running.");
  }

  const remainingActive = Array.isArray(activeEntrants) ? activeEntrants.length : 0;
  if (remainingActive <= 1) return alert("Only one player remains. Declare winner instead.");

  if (!eliminatorId) return alert("Select who eliminated this player.");
  if (eliminatorId === eliminatedEntrant.id) {
    return alert("Eliminator cannot be the same player.");
  }

  const eliminator = entrants.find((e) => e.id === eliminatorId);
  if (!eliminator || eliminator.eliminated) {
    return alert("Eliminator must be an active player.");
  }

  const eliminatorName = entrantLabel(eliminator);
  const finishPlace = remainingActive;

  try {
    const batch = writeBatch(db);

    const tRef = doc(db, "tournaments", tournamentId);
    const eliminatedRef = doc(db, "tournaments", tournamentId, "entrants", eliminatedEntrant.id);

    batch.update(eliminatedRef, {
      eliminated: true,
      winner: false,
      eliminatedAt: serverTimestamp(),
      finishPlace,
      eliminatedByUid: eliminator.uid || eliminator.id,
      eliminatedByName: eliminatorName,
      updatedAt: serverTimestamp(),
    });

    batch.update(tRef, {
      playersRemaining: increment(-1),
      updatedAt: serverTimestamp(),
    });

    const eliminatedName = entrantLabel(eliminatedEntrant);
    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "elimination",
      title: `${eliminatorName} eliminated ${eliminatedName}`,
      playerUid: eliminatedEntrant?.uid || eliminatedEntrant?.id || null,
      playerName: eliminatedName,
      relatedPlayerUid: eliminator?.uid || eliminator?.id || null,
      relatedPlayerName: eliminatorName,
      message: `${eliminatedName} finished in ${finishPlace}${finishPlace === 1 ? "st" : finishPlace === 2 ? "nd" : finishPlace === 3 ? "rd" : "th"} place.`,
      metadata: { finishPlace },
    });

    await batch.commit();

    setTournament((prev) => ({
      ...prev,
      playersRemaining: Math.max(0, Number(prev?.playersRemaining ?? remainingActive) - 1),
    }));
  } catch (err) {
    console.error("Eliminate failed:", err);
    alert("Could not eliminate player. Check permissions.");
  }
}

export async function declareWinner({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  entrants,
  tournament,
  clock,
  winner,
  entrantLabel,
  setTournament,
}) {
  if (!isAdmin || isCompleted) return;
  if (!winner) return;

  try {
    const active = entrants.filter((e) => !e.eliminated);
    if (active.length !== 2) {
      return alert("Winner can only be declared when 2 players remain.");
    }

    const runnerUp = active.find((p) => p.id !== winner.id);
    if (!runnerUp) return alert("Could not find runner-up.");

    const winnerName = entrantLabel(winner);

    const {
      paidEntryCount,
      totalRebuys,
      totalAddOns,
      finalPrizePool,
    } = computePrizePoolFromEntrants({
      tournament,
      entrants,
    });

    const firstPlacePrize = computeFirstPlacePrizeFromTournament({
      tournament,
      finalPrizePool,
    });

    const batch = writeBatch(db);

    const tRef = doc(db, "tournaments", tournamentId);
    const winnerRef = doc(db, "tournaments", tournamentId, "entrants", winner.id);
    const runnerRef = doc(db, "tournaments", tournamentId, "entrants", runnerUp.id);

    batch.update(winnerRef, {
      eliminated: false,
      finishPlace: 1,
      winner: true,
      updatedAt: serverTimestamp(),
    });

    batch.update(runnerRef, {
      eliminated: true,
      winner: false,
      finishPlace: 2,
      eliminatedByUid: winner.uid || winner.id,
      eliminatedByName: winnerName,
      eliminatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    batch.update(tRef, {
      status: "completed",
      entrantsCount: getPositiveInt(tournament?.entrantsCount || entrants.length, entrants.length),
      paidEntryCount,
      playersRemaining: 1,
      totalRebuys,
      totalAddOns,
      finalPrizePool,
      firstPlacePrize,
      addOnPause: deleteField(),
      tableCombine: deleteField(),
      clock: {
        status: "stopped",
        index: clock.index || 0,
        startedAt: null,
        endsAt: null,
        pausedAt: null,
        remainingMs: null,
      },
      results: {
        winnerUid: winner.uid || winner.id,
        winnerName,
        runnerUpUid: runnerUp.uid || runnerUp.id,
        runnerUpName: entrantLabel(runnerUp),
        completedAt: Timestamp.now(),
      },
      updatedAt: serverTimestamp(),
    });

    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "winner_declared",
      title: `${winnerName} won the tournament`,
      playerUid: winner?.uid || winner?.id || null,
      playerName: winnerName,
      relatedPlayerUid: runnerUp?.uid || runnerUp?.id || null,
      relatedPlayerName: entrantLabel(runnerUp),
      message: `${entrantLabel(runnerUp)} finished second. Final prize pool: $${Number(finalPrizePool || 0).toFixed(2)}.`,
      metadata: { finalPrizePool, firstPlacePrize },
    });

    await batch.commit();

    setTournament((prev) => ({
      ...prev,
      status: "completed",
      paidEntryCount,
      playersRemaining: 1,
      totalRebuys,
      totalAddOns,
      finalPrizePool,
      firstPlacePrize,
    }));

    alert(`Winner declared: ${winnerName}`);
  } catch (err) {
    console.error("Declare winner failed:", err);
    alert("Could not declare winner.");
  }
}

export async function assignSeats({
  db,
  tournamentId,
  isAdmin,
  isCompleted,
  entrants,
  shuffleArray,
  seatRoleLabel,
  setAssigningSeatsState,
}) {
  if (!isAdmin || isCompleted) return;

  const active = entrants.filter((e) => !e.eliminated);
  if (active.length === 0) return alert("No active entrants to seat.");
  if (active.length === 1) return alert("Only one entrant is active.");

  setAssigningSeatsState(true);

  try {
    const tournamentRef = doc(db, "tournaments", tournamentId);
    const tournamentSnap = await getDoc(tournamentRef);
    const tournament = tournamentSnap.exists() ? tournamentSnap.data() || {} : {};

    const maxPlayersPerTable = Math.max(
      2,
      getPositiveInt(tournament?.maxPlayersPerTable || 9, 9)
    );

    const shuffled = shuffleArray(active);

    const total = shuffled.length;
    const tablesCount = Math.ceil(total / maxPlayersPerTable);
    const baseSize = Math.floor(total / tablesCount);
    const remainder = total % tablesCount;

    const tables = [];
    let cursor = 0;

    for (let t = 0; t < tablesCount; t += 1) {
      const size = baseSize + (t < remainder ? 1 : 0);
      tables.push(shuffled.slice(cursor, cursor + size));
      cursor += size;
    }

    const batch = writeBatch(db);

    tables.forEach((tableEntrants, tableIndex) => {
      const tableNumber = tableIndex + 1;
      tableEntrants.forEach((player, seatIndex) => {
        const seatNumber = seatIndex + 1;
        const entrantRef = doc(db, "tournaments", tournamentId, "entrants", player.id);
        batch.update(entrantRef, {
          tableNumber,
          seatNumber,
          seatLabel: seatRoleLabel(seatNumber),
          seatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
    });

    batch.set(
      tournamentRef,
      {
        seatsAssignedAt: serverTimestamp(),
        tablesCount: tables.length,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    addEventToBatch(batch, {
      db,
      tournamentId,
      type: "seats_assigned",
      title: `Seats assigned across ${tables.length} table${tables.length === 1 ? "" : "s"}`,
      message: `${active.length} active players were seated with a maximum of ${maxPlayersPerTable} players per table.`,
      metadata: { playersSeated: active.length, tablesCount: tables.length, maxPlayersPerTable },
    });

    await batch.commit();

    alert(
      `Seats assigned: ${active.length} players across ${tables.length} table(s).`
    );
  } catch (err) {
    console.error("Assign seats failed:", err);
    alert("Could not assign seats. Check permissions.");
  }

  setAssigningSeatsState(false);
}